// Money and labels for screens. Money arrives as integer paisa.

/** 125005 → "৳1,250.05"; whole taka drop the decimals: 6000 → "৳60". */
export function taka(paisa: number): string {
  const sign = paisa < 0 ? "-" : "";
  const abs = Math.abs(paisa);
  const whole = Math.floor(abs / 100).toLocaleString("en-US");
  const rest = abs % 100;
  return `${sign}৳${whole}${rest ? `.${String(rest).padStart(2, "0")}` : ""}`;
}

/** Western digits → Bangla digits, for the rider's Bangla screen: "৳1,250" → "৳১,২৫০". */
export const bnDigits = (s: string | number) => String(s).replace(/\d/g, (d) => "০১২৩৪৫৬৭৮৯"[Number(d)]!);

/** taka() in Bangla digits. */
export const takaBn = (paisa: number) => bnDigits(taka(paisa));

/** "45.50" or "45" (taka, as staff type it) → paisa; null if it is not a price. */
export function parseTaka(text: string): number | null {
  const m = /^\s*(\d{1,6})(?:\.(\d{1,2}))?\s*$/.exec(text);
  if (!m) return null;
  const paisa = Number(m[1]) * 100 + Number((m[2] ?? "").padEnd(2, "0"));
  return paisa > 0 ? paisa : null;
}

export function medicineLabel(m: { brandName: string; strength: string; dosageForm: string } | null, fallbackId: number): string {
  if (!m) return `Medicine #${fallbackId}`;
  return [m.brandName, m.strength, m.dosageForm].filter(Boolean).join(" · ");
}

export const STATUS_LABEL: Record<string, string> = {
  pending_payment: "Awaiting payment",
  payment_failed: "Payment failed",
  placed: "New",
  awaiting_customer: "Waiting for customer",
  confirmed: "Confirmed",
  ready: "Ready",
  assigned: "Assigned",
  picked_up: "Out for delivery",
  delivered: "Delivered",
  delivery_failed: "Delivery failed",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

/** Why ops cancels an order (stored as "ops_<code>"). */
export const OPS_CANCEL_REASONS: { code: string; label: string }[] = [
  { code: "customer_asked", label: "Customer asked to cancel" },
  { code: "duplicate", label: "Duplicate order" },
  { code: "cannot_reach", label: "Cannot reach the customer" },
  { code: "pharmacy_problem", label: "Pharmacy problem" },
  { code: "other", label: "Other" },
];

/** A reason code from any side, in words: "ops_duplicate" → "Duplicate order (ops)". */
export function reasonLabel(code: string | null): string {
  if (!code) return "";
  const ops = OPS_CANCEL_REASONS.find((r) => `ops_${r.code}` === code);
  if (ops) return `${ops.label} (ops)`;
  const reject = REJECT_REASONS.find((r) => r.code === code);
  if (reject) return reject.label;
  const other: Record<string, string> = {
    customer_cancelled: "Customer cancelled in the app",
    no_answer: "No answer to the substitute",
    nothing_left: "Nothing left after substitutes",
    not_home: "Nobody home",
    wrong_address: "Wrong address",
    refused: "Refused",
    unreachable: "Phone not answered",
  };
  return other[code] ?? code.replace(/_/g, " ");
}

/** An ISO time in Dhaka: "4 Nov, 3:05 PM". */
export function dhakaTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { timeZone: "Asia/Dhaka", day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true });
}

/** A Dhaka date "YYYY-MM-DD", `days` from today. */
export function dhakaDate(days = 0, now = Date.now()): string {
  return new Date(now + 6 * 3_600_000 + days * 86_400_000).toISOString().slice(0, 10);
}

export const REJECT_REASONS: { code: string; label: string }[] = [
  { code: "out_of_stock", label: "Out of stock" },
  { code: "rx_missing", label: "Prescription missing" },
  { code: "rx_invalid", label: "Prescription not valid" },
  { code: "closed", label: "Pharmacy closed" },
  { code: "other", label: "Other" },
];
